import React from 'react';
import {
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useOutlet,
  useParams,
  useRevalidator,
  useSubmit
} from 'react-router-dom';
import * as Headless from '@headlessui/react';
import { Await } from '@oysterjs/ui/async';
import { AnimatePresence } from 'motion/react';
import { EllipsisVerticalIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

import {
  LoggedInAgencyUserFieldsFragment,
  ProducerInformationFieldsFragment,
  ProducerInformationInput,
  CoiConfigurationMode,
  UpdateProducerInformationMutation,
  UpdateProducerInformationMutationVariables,
  UpdateProducerInformationDocument,
  UpdateAgencyUserPreferencesMutation,
  UpdateAgencyUserPreferencesMutationVariables,
  UpdateAgencyUserPreferencesDocument,
  CreateAgencyUserProducerInformationMutation,
  SignOutMutation,
  SignOutMutationVariables,
  SignOutDocument,
  type TeamSummaryFieldsFragment,
  useUpdateAgencyUserPreferencesMutation
} from '~/api/graphql';
import { MyTeamsSection } from './teams';
import {
  defaultVisibilityValueFromFragment,
  useDefaultVisibilityValue
} from '~/components/visibility/default-visibility';
import { VisibilitySelector } from '~/components/visibility/visibility-selector';
import {
  visibilityInputFromValue,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { Spinner } from '@oysterjs/ui/spinner';
import { ValidationError } from '@oysterjs/core/graphql';
import { ActionResult } from '@oysterjs/core/graphql/actions';

import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { Avatar } from '@oysterjs/ui/avatar';
import { client } from '~/api/client';
import {
  ProducerInformationForm,
  emptyForm,
  piToForm,
  producerInformationToUpdateInput
} from './agency';
import { Description, Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Select } from '@oysterjs/ui/select';
import { CheckIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { getInitials } from '~/components/settings/utils';
import { useThemePreference } from '~/hooks/use-theme-preference';
import type { ThemePreference } from '~/lib/theme';

const INTERVAL_OPTIONS = [
  { value: 1, label: '1 minute' },
  { value: 2, label: '2 minutes' },
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' }
];

function DesktopNotificationsSection({
  agencyUser
}: {
  agencyUser: LoggedInAgencyUserFieldsFragment;
}) {
  const prefs = agencyUser.preferences.desktopNotifications;
  const [enabled, setEnabled] = React.useState(prefs?.enabled ?? false);
  const [intervalMinutes, setIntervalMinutes] = React.useState(
    prefs?.workspaceCompletionIntervalMinutes ?? 5
  );
  const [saveState, setSaveState] = React.useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [permissionDenied, setPermissionDenied] = React.useState(
    'Notification' in window && Notification.permission === 'denied'
  );

  const [updatePreferences] = useUpdateAgencyUserPreferencesMutation({ client });

  const notificationsSupported = 'Notification' in window;

  const save = async (nextEnabled: boolean, nextInterval: number) => {
    setSaveState('saving');
    try {
      await updatePreferences({
        variables: {
          input: {
            desktopNotifications: {
              enabled: nextEnabled,
              workspaceCompletionIntervalMinutes: nextInterval
            }
          }
        }
      });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 3000);
    } catch {
      setSaveState('error');
    }
  };

  const handleToggle = async (next: boolean) => {
    if (next && notificationsSupported && Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      if (result === 'denied') {
        setPermissionDenied(true);
        return;
      }
    }
    setEnabled(next);
    await save(next, intervalMinutes);
  };

  const handleIntervalChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = Number(e.target.value);
    setIntervalMinutes(next);
    await save(enabled, next);
  };

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          Desktop Notifications
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Configure notifications for workspace activity in your browser.
        </p>
      </div>
      <div className="px-4 py-5 sm:px-6">
        <Fieldset>
          <FieldGroup>
            <SwitchField>
              <Label>Notify me when a workspace completes</Label>
              <Description>
                Receive a desktop notification when a workspace finishes working.
              </Description>
              <Switch
                color="blue"
                checked={enabled}
                onChange={handleToggle}
                disabled={!notificationsSupported || permissionDenied || saveState === 'saving'}
              />
            </SwitchField>

            {enabled && (
              <Field>
                <Label>Notify after working for at least</Label>
                <Description>
                  Only notify if the workspace has been working for longer than this interval.
                </Description>
                <Select
                  value={String(intervalMinutes)}
                  onChange={handleIntervalChange}
                  disabled={saveState === 'saving'}
                >
                  {INTERVAL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </FieldGroup>
        </Fieldset>

        {!notificationsSupported && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-300">
            <InformationCircleIcon className="size-4 shrink-0" />
            Desktop notifications are not supported in this browser.
          </p>
        )}

        {permissionDenied && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
            <InformationCircleIcon className="size-4 shrink-0" />
            Notification permission was denied. Enable it in your browser settings to use this
            feature.
          </p>
        )}

        {notificationsSupported && !permissionDenied && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
            <InformationCircleIcon className="size-4 shrink-0" />
            Desktop notifications only work while the individual workspace tab is open.
          </p>
        )}

        {saveState === 'saved' && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
            <CheckIcon className="size-4 shrink-0" />
            Preferences saved.
          </p>
        )}

        {saveState === 'error' && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
            <ExclamationCircleIcon className="size-4 shrink-0" />
            Failed to save. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}

const APPEARANCE_OPTIONS: { value: ThemePreference; label: string; description: string }[] = [
  { value: 'auto', label: 'Auto', description: 'Match your system settings.' },
  { value: 'light', label: 'Light', description: 'Always use the light theme.' },
  { value: 'dark', label: 'Dark', description: 'Always use the dark theme.' }
];

function AppearanceSection() {
  const [preference, setPreference] = useThemePreference();

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">Appearance</h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Choose how Cara looks on this device.
        </p>
      </div>
      <div className="px-4 py-5 sm:px-6">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Theme</Label>
              <Description>
                This is stored in your browser, so each device you sign in from can differ.
              </Description>
              <RadioGroup
                value={preference}
                onChange={(value) => setPreference(value as ThemePreference)}
              >
                {APPEARANCE_OPTIONS.map((option) => (
                  <RadioField key={option.value}>
                    <Radio value={option.value} color="sky" />
                    <Label>{option.label}</Label>
                    <Description>{option.description}</Description>
                  </RadioField>
                ))}
              </RadioGroup>
            </Field>
          </FieldGroup>
        </Fieldset>
      </div>
    </div>
  );
}

function DefaultVisibilitySection({
  agencyUser
}: {
  agencyUser: LoggedInAgencyUserFieldsFragment;
}) {
  const visibilityEnabled = useVisibilityEntitlement();
  const fallbackDefaultVisibility = useDefaultVisibilityValue();
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(() =>
    defaultVisibilityValueFromFragment(agencyUser.preferences.defaultVisibility, {
      disallowPrivate: agencyUser.agency.configuration.visibility.disallowPrivate
    })
  );
  const [saveState, setSaveState] = React.useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [updatePreferences] = useUpdateAgencyUserPreferencesMutation({ client });
  const revalidator = useRevalidator();

  React.useEffect(() => {
    setVisibility(fallbackDefaultVisibility);
  }, [fallbackDefaultVisibility]);

  if (!visibilityEnabled) {
    return null;
  }

  const handleChange = async (next: VisibilitySelectorValue) => {
    setVisibility(next);
    setSaveState('saving');
    try {
      await updatePreferences({
        variables: {
          input: {
            defaultVisibility: visibilityInputFromValue(next)
          }
        }
      });
      setSaveState('saved');
      revalidator.revalidate();
      setTimeout(() => setSaveState('idle'), 3000);
    } catch {
      setSaveState('error');
    }
  };

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          Default Sharing
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Choose the default visibility for new workspaces, presets, templates, and library items.
        </p>
      </div>
      <div className="px-4 py-5 sm:px-6">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Default visibility for new Cara items</Label>
              <Description>
                You can still change visibility on an individual item before creating it.
              </Description>
              <div className="mt-3">
                <VisibilitySelector
                  value={visibility}
                  onChange={handleChange}
                  disallowPrivate={agencyUser.agency.configuration.visibility.disallowPrivate}
                />
              </div>
            </Field>
          </FieldGroup>
        </Fieldset>

        {saveState === 'saving' && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
            <Spinner size="size-4" color="neutral" />
            Saving...
          </p>
        )}
        {saveState === 'saved' && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
            <CheckIcon className="size-4 shrink-0" />
            Preferences saved.
          </p>
        )}
        {saveState === 'error' && (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
            <ExclamationCircleIcon className="size-4 shrink-0" />
            Failed to save. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}

const ProfilePageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading profile...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

// ─── COI Configuration Mode Section ──────────────────────────────────────────

const COIConfigurationModeSection = ({
  agencyUser
}: {
  agencyUser: LoggedInAgencyUserFieldsFragment;
}) => {
  const revalidator = useRevalidator();
  const [saving, setSaving] = React.useState(false);

  const currentMode = agencyUser.preferences.overrideCOIConfigurationMode ?? null;

  const handleModeChange = async (mode: CoiConfigurationMode | null) => {
    setSaving(true);
    try {
      await client.mutate<
        UpdateAgencyUserPreferencesMutation,
        UpdateAgencyUserPreferencesMutationVariables
      >({
        mutation: UpdateAgencyUserPreferencesDocument,
        variables: {
          input: {
            overrideCOIConfigurationMode: mode ?? undefined
          }
        }
      });
      revalidator.revalidate();
    } finally {
      setSaving(false);
    }
  };

  const isUser = currentMode === CoiConfigurationMode.User;

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          Form Default Information
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Choose whether certificates and documents use agency-level or your personal producer
          information by default.
        </p>
      </div>
      <div className="px-4 py-5 sm:px-6">
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Default source for producer information</Label>
              <Description>
                This controls which producer information and signature are pre-filled when
                generating forms.
              </Description>
              <RadioGroup
                value={isUser ? CoiConfigurationMode.User : CoiConfigurationMode.Agency}
                onChange={(value) => handleModeChange(value as CoiConfigurationMode)}
                disabled={saving}
              >
                <RadioField>
                  <Radio value={CoiConfigurationMode.Agency} color="sky" />
                  <Label>Agency default</Label>
                  <Description>
                    Use the agency-level default producer information on generated forms.
                  </Description>
                </RadioField>
                <RadioField>
                  <Radio value={CoiConfigurationMode.User} color="sky" />
                  <Label>My information</Label>
                  <Description>
                    Use your personal default producer information and signature on generated forms.
                  </Description>
                </RadioField>
              </RadioGroup>
            </Field>
          </FieldGroup>
        </Fieldset>
        {saving && (
          <div className="mt-3 flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
            <Spinner size="size-4" color="neutral" />
            Saving...
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Producer Information Section ────────────────────────────────────────────

const UserProducerInformationSection = ({
  producerInformations
}: {
  producerInformations: ProducerInformationFieldsFragment[];
}) => {
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const handleMakeDefault = async (pi: ProducerInformationFieldsFragment) => {
    await client.mutate<
      UpdateProducerInformationMutation,
      UpdateProducerInformationMutationVariables
    >({
      mutation: UpdateProducerInformationDocument,
      variables: {
        id: pi.id,
        input: producerInformationToUpdateInput(pi, { isDefault: true })
      }
    });
    revalidator.revalidate();
  };

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
            Producer Information
          </h3>
          <Button color="sky" href="/app/settings/profile/producer-information/new">
            Create
          </Button>
        </div>
      </div>
      <div className="py-5">
        {producerInformations.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4 px-4 sm:px-6">
            No producer information configured. Create one to use in certificates and documents.
          </p>
        ) : (
          <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {producerInformations.map((pi) => (
              <li
                key={pi.id}
                className="flex justify-between px-4 py-4 sm:px-6 first:pt-0 last:pb-0"
              >
                <div className="flex min-w-0 gap-x-4">
                  <div className="size-12 flex-none rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center text-neutral-700 dark:text-neutral-300 font-semibold text-lg">
                    {pi.producerName
                      .split(' ')
                      .filter((w) => w.length > 0)
                      .map((w) => w[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm/6 font-semibold text-neutral-900 dark:text-white">
                      {pi.producerName}
                      {pi.isDefault && (
                        <span className="ml-2">
                          <Badge color="sky">Default</Badge>
                        </span>
                      )}
                    </p>
                    <p className="flex text-xs/5 text-neutral-500 dark:text-neutral-400">
                      {pi.agencyName}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-x-6">
                  <Headless.Menu as="div" className="relative flex-none">
                    <Headless.MenuButton className="-m-2.5 block p-2.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white">
                      <span className="sr-only">Open options</span>
                      <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
                    </Headless.MenuButton>
                    <Headless.MenuItems
                      transition
                      className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white dark:bg-neutral-800 py-2 shadow-lg ring-1 ring-neutral-900/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                    >
                      {!pi.isDefault && (
                        <Headless.MenuItem>
                          <span
                            role="button"
                            onClick={() => handleMakeDefault(pi)}
                            className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none cursor-pointer"
                          >
                            Make default
                          </span>
                        </Headless.MenuItem>
                      )}
                      <Headless.MenuItem>
                        <span
                          role="button"
                          onClick={() =>
                            navigate(`/app/settings/profile/producer-information/${pi.id}/edit`)
                          }
                          className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none cursor-pointer"
                        >
                          Edit
                        </span>
                      </Headless.MenuItem>
                    </Headless.MenuItems>
                  </Headless.Menu>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ─── Profile Page ─────────────────────────────────────────────────────────────

export const SettingsProfilePage = () => {
  const navigate = useNavigate();
  const outlet = useOutlet();
  const loader = useLoaderData() as {
    data: Promise<{
      agencyUser: LoggedInAgencyUserFieldsFragment;
      producerInformations: ProducerInformationFieldsFragment[];
      myTeams?: TeamSummaryFieldsFragment[];
    }>;
  };

  const signOut = async () => {
    const res = await client.mutate<SignOutMutation, SignOutMutationVariables>({
      mutation: SignOutDocument
    });
    if (res.data?.agencySignOut) {
      navigate('/app/signin');
    }
  };

  return (
    <React.Suspense fallback={<ProfilePageLoader />}>
      <Await resolve={loader.data}>
        {(data) =>
          data?.agencyUser && (
            <div className="space-y-6">
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
                    Your Profile
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    Your account information and preferences
                  </p>
                </div>
                <div className="px-4 py-5 sm:px-6">
                  <div className="flex items-center gap-4">
                    <Avatar
                      initials={getInitials(data.agencyUser.name)}
                      alt={data.agencyUser.name}
                      className="h-16 w-16 text-2xl"
                    />
                    <div className="min-w-0 flex-1 items-center">
                      <h2 className="mt-0 text-xl font-semibold text-neutral-900 dark:text-white">
                        {data.agencyUser.name}
                      </h2>
                      <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
                        {data.agencyUser.email}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="px-4 py-5 sm:px-6">
                  <Button color="white" onClick={signOut}>
                    Sign out
                  </Button>
                </div>
              </div>
              <AppearanceSection />

              <DesktopNotificationsSection agencyUser={data.agencyUser} />

              <DefaultVisibilitySection agencyUser={data.agencyUser} />

              <MyTeamsSection teams={data.myTeams ?? []} />

              <COIConfigurationModeSection agencyUser={data.agencyUser} />

              <UserProducerInformationSection
                producerInformations={data.producerInformations ?? []}
              />

              <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet)}</AnimatePresence>
            </div>
          )
        }
      </Await>
    </React.Suspense>
  );
};

// ─── Create Page ──────────────────────────────────────────────────────────────

export const UserProducerInformationCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const submit = useSubmit();
  const navigation = useNavigation();
  const action = useActionData() as
    | ActionResult<CreateAgencyUserProducerInformationMutation>
    | undefined;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/settings/profile');
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = (input: ProducerInformationInput) => {
    setValidationErrors([]);
    submit(input, { method: 'POST', encType: 'application/json' });
  };

  const error = action?.applicationError?.message || action?.serverError?.message || null;

  return (
    <MountableSlideout onClose={onClose} title="Create Producer Information">
      <ProducerInformationForm
        initialValues={emptyForm()}
        existingSignatureIsDrawn={false}
        onSubmit={handleSubmit}
        onClose={onClose}
        loading={navigation.state === 'submitting'}
        error={error}
        validationErrors={validationErrors}
      />
    </MountableSlideout>
  );
};

// ─── Edit Page ────────────────────────────────────────────────────────────────

export const UserProducerInformationEditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const submit = useSubmit();
  const navigation = useNavigation();
  const action = useActionData() as ActionResult<UpdateProducerInformationMutation> | undefined;
  const loader = useLoaderData() as
    | { data: Promise<{ producerInformations: ProducerInformationFieldsFragment[] }> }
    | undefined;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/settings/profile');
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = (input: ProducerInformationInput) => {
    if (!id) return;
    setValidationErrors([]);
    submit(input, { method: 'PUT', encType: 'application/json' });
  };

  const error = action?.applicationError?.message || action?.serverError?.message || null;

  if (!loader) return null;

  return (
    <React.Suspense fallback={null}>
      <Await resolve={loader.data}>
        {(data) => {
          const pi = data?.producerInformations?.find((p) => p.id === id);
          if (!pi) return null;
          const existingSignatureIsDrawn = !!pi.producerSignatureJpegBase64;
          return (
            <MountableSlideout onClose={onClose} title="Edit Producer Information">
              <ProducerInformationForm
                initialValues={piToForm(pi)}
                existingSignatureIsDrawn={existingSignatureIsDrawn}
                onSubmit={handleSubmit}
                onClose={onClose}
                loading={navigation.state === 'submitting'}
                error={error}
                validationErrors={validationErrors}
              />
            </MountableSlideout>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
